import { useState, useEffect } from "react";
import {
  CheckCircle,
  XCircle,
  Clock,
  StopCircle,
  Gavel,
  ArrowRight,
  FileText,
  Trash2,
  X,
  Calendar,
  PackageCheck,
  AlertTriangle,
  Banknote,
} from "lucide-react";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { AuctionConfirmModal } from "./AuctionConfirmModal";

interface AuctionConfirmModalState {
  isOpen: boolean;
  title: string;
  description: string;
  onConfirm: () => void;
  confirmLabel?: string;
  variant?: "default" | "destructive";
  icon?: React.ReactNode;
}

interface AuctionStatusActionsProps {
  status: string;
  isPendingReview: boolean;
  isLive: boolean;
  canStartAuction: boolean;
  isPendingSellerDecision: boolean;
  isNoBids: boolean;
  isSold: boolean;
  canDeleteAuction: boolean;
  pausing: boolean;
  starting: boolean;
  deleting: boolean;
  startActionLabel: string;
  contractsEnabled: boolean;
  sendingPurchaseContract: boolean;
  purchaseContractSuccess?: string | null;
  purchaseContractError?: string | null;
  sendingInvoice: boolean;
  invoiceSuccess?: string | null;
  invoiceError?: string | null;
  onPost: (path: string, body?: any) => Promise<any>;
  onPatch: (path: string, body?: any) => Promise<any>;
  onDelete: () => Promise<void>;
  onSendPurchaseContract: () => Promise<void>;
  onSendInvoice: () => Promise<void>;
  onOpenNegotiation: () => void;
  onUpdateData: (fields: any) => void;
  type?: "regular" | "flash" | null;
  durationMinutes?: number | null;
}

export function AuctionStatusActions({
  status,
  isPendingReview,
  isLive,
  canStartAuction,
  isPendingSellerDecision,
  isNoBids,
  isSold,
  canDeleteAuction,
  pausing,
  starting,
  deleting,
  startActionLabel,
  contractsEnabled,
  sendingPurchaseContract,
  purchaseContractSuccess,
  purchaseContractError,
  sendingInvoice,
  invoiceSuccess,
  invoiceError,
  onPost,
  onPatch,
  onDelete,
  onSendPurchaseContract,
  onSendInvoice,
  onOpenNegotiation,
  onUpdateData,
  type = "regular",
  durationMinutes = 1440,
}: AuctionStatusActionsProps) {
  const [adminType, setAdminType] = useState<"regular" | "flash">(type === "flash" ? "flash" : "regular");
  const [adminDuration, setAdminDuration] = useState<number>(durationMinutes || 1440);
  const [selectedEndsAt, setSelectedEndsAt] = useState<string>("");
  const [actingStatus, setActingStatus] = useState<string | null>(null);
  const [confirmModal, setConfirmModal] = useState<AuctionConfirmModalState>({
    isOpen: false,
    title: "",
    description: "",
    onConfirm: () => {},
  });

  const closeConfirm = () => setConfirmModal(prev => ({ ...prev, isOpen: false }));

  useEffect(() => {
    const now = new Date();
    const end = new Date(now.getTime() + adminDuration * 60000);
    const year = end.getFullYear();
    const month = String(end.getMonth() + 1).padStart(2, "0");
    const day = String(end.getDate()).padStart(2, "0");
    const hours = String(end.getHours()).padStart(2, "0");
    const mins = String(end.getMinutes()).padStart(2, "0");
    setSelectedEndsAt(`${year}-${month}-${day}T${hours}:${mins}`);
  }, [adminDuration]);

  async function transitionStatus(targetStatus: string) {
    setActingStatus(targetStatus);
    try {
      const r = await onPatch("", { status: targetStatus });
      if (r) onUpdateData({ status: targetStatus });
    } finally {
      setActingStatus(null);
    }
  }

  async function confirmPayout() {
    setActingStatus("paid_out");
    try {
      const r = await onPost("/confirm-payout");
      if (r) onUpdateData({ status: "pending_payout" }); // job is queued; status transitions async
    } finally {
      setActingStatus(null);
    }
  }

  return (
    <div className="space-y-4">
      {/* 1. Review Actions */}
      {isPendingReview && (
        <Card className="border-warning/40 shadow-sm bg-warning/5">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold">Til godkjenning</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="p-3 rounded-lg bg-background border border-border/50 mb-3 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-[10px] uppercase font-bold text-muted-foreground">Auksjonstype</span>
                <div className="flex bg-muted rounded-md p-0.5">
                  <button
                    className={`px-3 py-1 text-[10px] font-bold rounded-sm transition-all ${adminType === "regular" ? "bg-white shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground"}`}
                    onClick={() => { setAdminType("regular"); setAdminDuration(1440); }}
                  >
                    Vanlig
                  </button>
                  <button
                    className={`px-3 py-1 text-[10px] font-bold rounded-sm transition-all ${adminType === "flash" ? "bg-accent text-white shadow-sm" : "text-muted-foreground hover:text-foreground"}`}
                    onClick={() => { setAdminType("flash"); setAdminDuration(15); }}
                  >
                    Lyn
                  </button>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-[10px] uppercase font-bold text-muted-foreground">Varighet</span>
                <select 
                  className="bg-transparent text-[10px] font-bold focus:outline-none cursor-pointer"
                  value={adminDuration}
                  onChange={(e) => setAdminDuration(Number(e.target.value))}
                >
                  {adminType === "flash" ? (
                    <>
                      <option value={15}>15 minutter</option>
                      <option value={60}>1 time</option>
                      <option value={120}>2 timer</option>
                    </>
                  ) : (
                    <>
                      <option value={1440}>24 timer</option>
                      <option value={2880}>48 timer</option>
                      <option value={4320}>72 timer</option>
                    </>
                  )}
                </select>
              </div>
            </div>

            <Button className="w-full gap-2" onClick={async () => {
              const r = await onPatch("/review", { 
                decision: "approved",
                type: adminType,
                durationMinutes: adminDuration
              });
              if (r) onUpdateData({ status: r.status, type: adminType, durationMinutes: adminDuration });
            }}>
              <CheckCircle className="h-3.5 w-3.5" /> Godkjenn
            </Button>
            <Button variant="outline" className="w-full gap-2 bg-background" onClick={async () => {
              const r = await onPatch("/review", { decision: "changes_requested" });
              if (r) onUpdateData({ status: r.status });
            }}>
              <XCircle className="h-3.5 w-3.5" /> Send tilbake
            </Button>
          </CardContent>
        </Card>
      )}

      {/* No bids info */}
      {isNoBids && (
        <Card className="border-warning/40 bg-warning/5 shadow-sm">
          <CardContent className="pt-4 pb-4">
            <p className="text-sm font-semibold">Ingen bud mottatt</p>
            <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
              Auksjonen ble avsluttet uten bud. Du kan relansere auksjonen eller slette den.
            </p>
          </CardContent>
        </Card>
      )}

      {/* 2. Live & Start Controls */}
      {(isLive || canStartAuction) && (
        <Card className="shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold">Kontroller</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {isLive && (
              <div className="space-y-4 animate-in fade-in duration-500">
                <div className="p-4 rounded-xl bg-primary/5 border border-primary/10 shadow-inner group">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="p-1.5 rounded-lg bg-primary/10 text-primary">
                      <Clock className="h-3.5 w-3.5" />
                    </div>
                    <p className="text-[10px] uppercase font-bold tracking-widest text-primary/80">Hurtigforlengelse</p>
                  </div>
                  
                  <div className="grid grid-cols-3 gap-2">
                    {[
                      { label: "+30s", value: { seconds: 30 } },
                      { label: "+2m", value: { minutes: 2 } },
                      { label: "+30m", value: { minutes: 30 } },
                      { label: "+1h", value: { minutes: 60 } },
                      { label: "+2h", value: { minutes: 120 } },
                      { label: "+12h", value: { minutes: 720 } },
                    ].map((opt) => (
                      <Button 
                        key={opt.label} 
                        variant="outline" 
                        size="sm" 
                        className="text-[10px] h-8 font-bold hover:bg-primary hover:text-white hover:border-primary transition-all active:scale-95" 
                        onClick={async () => {
                          const r = await onPost("/extend", opt.value);
                          if (r) onUpdateData({ endsAt: r.endsAt });
                        }}
                      >
                        {opt.label}
                      </Button>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-2 pt-2 border-t border-border/50">
                  <Button 
                    variant="outline" 
                    className="gap-2 h-10 font-bold hover:bg-amber-500/10 hover:text-amber-600 hover:border-amber-500/20 transition-all active:scale-95" 
                    disabled={pausing} 
                    onClick={() => {
                      setConfirmModal({
                        isOpen: true,
                        title: "Pause auksjon",
                        description: "Er du sikker på at du vil pause auksjonen? Budgivning vil bli midlertidig deaktivert.",
                        icon: <Clock className="h-8 w-8 text-amber-500" />,
                        onConfirm: async () => {
                          closeConfirm();
                          const r = await onPost("/pause");
                          if (r) onUpdateData({ status: r.status });
                        }
                      });
                    }}
                  >
                    <Clock className="h-4 w-4" /> {pausing ? "Pauser..." : "Pause"}
                  </Button>
                  <Button 
                    variant="destructive" 
                    className="gap-2 h-10 font-bold shadow-lg shadow-destructive/10 transition-all hover:translate-y-[-1px] active:translate-y-[0px]" 
                    onClick={() => {
                      setConfirmModal({
                        isOpen: true,
                        variant: "destructive",
                        title: "Stopp auksjon",
                        description: "Er du sikker på at du vil stoppe auksjonen nå? Dette vil lukke auksjonen for godt og kåre en eventuell vinner.",
                        icon: <StopCircle className="h-8 w-8" />,
                        onConfirm: async () => {
                          closeConfirm();
                          const r = await onPost("/stop");
                          if (r) onUpdateData({ status: r.status, closedAt: r.closedAt });
                        }
                      });
                    }}
                  >
                    <StopCircle className="h-4 w-4" /> Stopp
                  </Button>
                </div>
              </div>
            )}
            {canStartAuction && (
              <div className="space-y-4 pt-4 border-t border-border/50">
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 rounded-lg bg-background border border-border/50">
                    <div className="flex items-center gap-1.5 text-[10px] uppercase font-bold tracking-wider text-muted-foreground">
                      <span>Valg</span>
                    </div>
                    <div className="flex bg-muted rounded-md p-0.5">
                      <button
                        className={`px-3 py-1 text-[10px] font-bold rounded-sm transition-all ${adminType === "regular" ? "bg-white shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground"}`}
                        onClick={() => { setAdminType("regular"); setAdminDuration(1440); }}
                      >
                        Vanlig
                      </button>
                      <button
                        className={`px-3 py-1 text-[10px] font-bold rounded-sm transition-all ${adminType === "flash" ? "bg-accent text-white shadow-sm" : "text-muted-foreground hover:text-foreground"}`}
                        onClick={() => { setAdminType("flash"); setAdminDuration(15); }}
                      >
                        Lyn
                      </button>
                    </div>
                  </div>

                  <div className="flex items-center justify-between p-3 rounded-lg bg-background/50 border border-border/50">
                    <div className="flex items-center gap-1.5 text-[10px] uppercase font-bold tracking-wider text-muted-foreground">
                      <Clock className="h-3 w-3" />
                      <span>Varighet</span>
                    </div>
                    <select 
                      className="bg-transparent text-[10px] font-bold focus:outline-none cursor-pointer"
                      value={adminDuration}
                      onChange={(e) => setAdminDuration(Number(e.target.value))}
                    >
                      {adminType === "flash" ? (
                        <>
                          <option value={15}>15 minutter</option>
                          <option value={60}>1 time</option>
                          <option value={120}>2 timer</option>
                        </>
                      ) : (
                        <>
                          <option value={1440}>24 timer</option>
                          <option value={2880}>48 timer</option>
                          <option value={4320}>72 timer</option>
                        </>
                      )}
                    </select>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1.5 text-[10px] uppercase font-bold tracking-wider text-muted-foreground">
                      <Calendar className="h-3 w-3" />
                      <span>Sett sluttid</span>
                    </div>
                    <div className="flex gap-1">
                      {[1, 6, 24].map(hours => (
                        <button
                          key={hours}
                          type="button"
                          className="text-[10px] px-2 py-1 rounded bg-muted/50 hover:bg-muted font-bold transition-all border border-transparent hover:border-border text-muted-foreground hover:text-foreground"
                          onClick={() => {
                            const baseDate = selectedEndsAt ? new Date(selectedEndsAt) : new Date();
                            baseDate.setHours(baseDate.getHours() + hours);
                            const year = baseDate.getFullYear();
                            const month = String(baseDate.getMonth() + 1).padStart(2, "0");
                            const day = String(baseDate.getDate()).padStart(2, "0");
                            const hoursStr = String(baseDate.getHours()).padStart(2, "0");
                            const minutesStr = String(baseDate.getMinutes()).padStart(2, "0");
                            setSelectedEndsAt(`${year}-${month}-${day}T${hoursStr}:${minutesStr}`);
                          }}
                        >
                          +{hours}h
                        </button>
                      ))}
                      {selectedEndsAt && (
                        <button
                          type="button"
                          title="Nullstill"
                          className="text-[10px] px-2 py-1 rounded bg-red-500/10 text-red-600 hover:bg-red-500/20 font-bold transition-all border border-transparent ml-1"
                          onClick={() => setSelectedEndsAt("")}
                        >
                          <X className="h-3 w-3" />
                        </button>
                      )}
                    </div>
                  </div>
                  
                  <div className="relative group">
                    <input
                      type="datetime-local"
                      className="flex h-10 w-full rounded-lg border border-input bg-background/50 px-3 py-2 text-xs shadow-sm transition-all focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring hover:bg-background focus:bg-background pr-10"
                      value={selectedEndsAt}
                      onChange={(e) => setSelectedEndsAt(e.target.value)}
                    />
                    <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-muted-foreground group-focus-within:text-primary transition-colors">
                      <Calendar className="h-4 w-4" />
                    </div>
                  </div>
                  
                  {selectedEndsAt && (
                    <div className="flex items-center gap-2 px-2 py-1.5 rounded-md bg-primary/5 border border-primary/10 animate-in fade-in slide-in-from-top-1">
                      <CheckCircle className="h-3 w-3 text-primary" />
                      <p className="text-[10px] font-medium text-primary">
                        Auksjonen avsluttes {new Date(selectedEndsAt).toLocaleString("nb-NO", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                      </p>
                    </div>
                  )}
                </div>

                <Button
                  className="w-full gap-2 h-10 font-bold shadow-lg shadow-primary/10 transition-all hover:translate-y-[-1px] active:translate-y-[0px]"
                  disabled={starting}
                  onClick={() => {
                    const params: any = {};
                    if (selectedEndsAt) {
                      params.endsAt = new Date(selectedEndsAt).toISOString();
                    }

                    const formattedEndsAt = selectedEndsAt 
                      ? new Date(selectedEndsAt).toLocaleString("nb-NO", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })
                      : "automatisk beregnet tid";

                    setConfirmModal({
                      isOpen: true,
                      title: startActionLabel,
                      description: `Er du sikker på at du vil starte auksjonen nå?\n\nSluttid er satt til: ${formattedEndsAt}`,
                      icon: <Gavel className="h-8 w-8" />,
                      confirmLabel: "Start auksjon",
                      onConfirm: async () => {
                        closeConfirm();
                        const r = await onPost("/start", {
                          ...params,
                          type: adminType,
                          durationMinutes: adminDuration
                        });
                        if (r) {
                          onUpdateData({
                            status: r.status,
                            startsAt: r.startsAt,
                            publishedAt: r.publishedAt,
                            endsAt: r.endsAt,
                            closedAt: null,
                            type: adminType,
                            durationMinutes: adminDuration
                          });
                        }
                      }
                    });
                  }}
                >
                  <Gavel className="h-4 w-4" /> {starting ? "Starter..." : startActionLabel}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* 3. Negotiation Nudge */}
      {isPendingSellerDecision && (
        <Card className="border-warning/40 bg-warning/5 shadow-sm">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-sm font-semibold">Venter på beslutning</p>
                <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
                  Høyeste bud venter på selgers svar.
                </p>
              </div>
              <Button
                size="sm"
                variant="ghost"
                className="h-8 text-xs font-semibold text-primary hover:text-primary hover:bg-primary/5 gap-1 group"
                onClick={onOpenNegotiation}
              >
                Åpne <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* 4. Post-Auction Actions (Sold / Invoicing) */}
      {isSold && contractsEnabled && (
        <Card className="border-green-200 bg-green-50/40 shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold">Kjøpskontrakt</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-[11px] text-muted-foreground leading-relaxed italic">
              Auksjonen er markert som solgt. Send kontrakten til vinneren.
            </p>
            <Button
              className="w-full gap-2 h-9"
              disabled={sendingPurchaseContract}
              onClick={onSendPurchaseContract}
            >
              <CheckCircle className="h-3.5 w-3.5" />
              {sendingPurchaseContract ? "Sender..." : "Send kjøpskontrakt"}
            </Button>
            {purchaseContractSuccess && (
              <p className="text-[11px] font-medium text-green-700 animate-in fade-in slide-in-from-top-1">{purchaseContractSuccess}</p>
            )}
            {purchaseContractError && (
              <p className="text-[11px] font-medium text-destructive animate-in fade-in slide-in-from-top-1">{purchaseContractError}</p>
            )}
          </CardContent>
        </Card>
      )}

      {(status === "invoice_pending" || (isSold && !contractsEnabled)) && (
        <Card className="border-amber-200 bg-amber-50/40 shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold">Fakturering</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-[11px] text-muted-foreground leading-relaxed italic">
              Klargjør og send faktura for bilsalget og tilhørende ordrelinjer.
            </p>
            <Button
              className="w-full gap-2 h-9"
              disabled={sendingInvoice}
              onClick={onSendInvoice}
            >
              <FileText className="h-3.5 w-3.5" />
              {sendingInvoice ? "Sender..." : "Send faktura via Fiken"}
            </Button>
            {invoiceSuccess && (
              <p className="text-[11px] font-medium text-amber-700 animate-in fade-in slide-in-from-top-1">{invoiceSuccess}</p>
            )}
            {invoiceError && (
              <p className="text-[11px] font-medium text-destructive animate-in fade-in slide-in-from-top-1">{invoiceError}</p>
            )}
            <div className="border-t border-border/50 pt-3">
              <p className="text-[10px] uppercase font-bold text-muted-foreground mb-2">Manuelt</p>
              <Button
                variant="outline"
                className="w-full gap-2 h-9 bg-background"
                disabled={!!actingStatus}
                onClick={() => {
                  setConfirmModal({
                    isOpen: true,
                    title: "Merk som fakturert",
                    description: "Bekreft at faktura er sendt manuelt utenfor systemet. Statusen settes til «Avventer betaling».",
                    icon: <FileText className="h-8 w-8 text-amber-600" />,
                    confirmLabel: "Bekreft",
                    onConfirm: async () => {
                      closeConfirm();
                      await transitionStatus("payment_pending");
                    },
                  });
                }}
              >
                <FileText className="h-3.5 w-3.5" />
                {actingStatus === "payment_pending" ? "Lagrer..." : "Merk som fakturert manuelt"}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* 4b. Payment pending — confirm payment received */}
      {status === "payment_pending" && (
        <Card className="border-amber-200 bg-amber-50/40 shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold">Avventer betaling</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-[11px] text-muted-foreground leading-relaxed italic">
              Faktura er sendt til kjøper. Registrer manuelt når betaling er mottatt.
            </p>
            <Button
              className="w-full gap-2 h-9"
              disabled={!!actingStatus}
              onClick={() => {
                setConfirmModal({
                  isOpen: true,
                  title: "Registrer betaling",
                  description: "Bekreft at betaling er mottatt fra kjøper. Statusen settes til «Klar til å hente».",
                  icon: <Banknote className="h-8 w-8 text-green-600" />,
                  confirmLabel: "Bekreft betaling",
                  onConfirm: async () => {
                    closeConfirm();
                    await transitionStatus("ready_for_pickup");
                  },
                });
              }}
            >
              <Banknote className="h-3.5 w-3.5" />
              {actingStatus === "ready_for_pickup" ? "Lagrer..." : "Registrer betaling mottatt"}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* 5. Ready for pickup */}
      {status === "ready_for_pickup" && (
        <Card className="border-blue-200 bg-blue-50/40 shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold">Henting</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-[11px] text-muted-foreground leading-relaxed italic">
              Bekreft at kjøper har hentet kjøretøyet.
            </p>
            <Button
              className="w-full gap-2 h-9"
              disabled={actingStatus === "picked_up"}
              onClick={() => {
                setConfirmModal({
                  isOpen: true,
                  title: "Bekreft henting",
                  description: "Er du sikker på at kjøper har hentet kjøretøyet?",
                  icon: <PackageCheck className="h-8 w-8 text-blue-600" />,
                  confirmLabel: "Bekreft henting",
                  onConfirm: async () => {
                    closeConfirm();
                    await transitionStatus("picked_up");
                  },
                });
              }}
            >
              <PackageCheck className="h-3.5 w-3.5" />
              {actingStatus === "picked_up" ? "Lagrer..." : "Bekreft henting"}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* 6. Picked up — set in transit */}
      {status === "picked_up" && (
        <Card className="border-blue-200 bg-blue-50/40 shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold">Transport</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <p className="text-[11px] text-muted-foreground leading-relaxed italic">
              Transportør har hentet kjøretøyet. Bekreft at bilen er under transport.
            </p>
            <Button
              className="w-full gap-2 h-9"
              disabled={actingStatus === "in_transit"}
              onClick={() => {
                setConfirmModal({
                  isOpen: true,
                  title: "Sett under transport",
                  description: "Bekreft at kjøretøyet er på vei til kjøper.",
                  icon: <PackageCheck className="h-8 w-8 text-blue-600" />,
                  confirmLabel: "Bekreft",
                  onConfirm: async () => {
                    closeConfirm();
                    await transitionStatus("in_transit");
                  },
                });
              }}
            >
              <PackageCheck className="h-3.5 w-3.5" />
              {actingStatus === "in_transit" ? "Lagrer..." : "Sett under transport"}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* 6b. In transit — confirm delivery */}
      {status === "in_transit" && (
        <Card className="border-blue-200 bg-blue-50/40 shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold">Under transport</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <p className="text-[11px] text-muted-foreground leading-relaxed italic">
              Kjøretøyet er under transport. Bekreft levering når bilen er fremme.
            </p>
            <Button
              className="w-full gap-2 h-9"
              disabled={actingStatus === "delivered"}
              onClick={() => {
                setConfirmModal({
                  isOpen: true,
                  title: "Bekreft levering",
                  description: "Er du sikker på at kjøretøyet er levert til kjøper?",
                  icon: <PackageCheck className="h-8 w-8 text-green-600" />,
                  confirmLabel: "Bekreft levering",
                  onConfirm: async () => {
                    closeConfirm();
                    await transitionStatus("delivered");
                  },
                });
              }}
            >
              <PackageCheck className="h-3.5 w-3.5" />
              {actingStatus === "delivered" ? "Lagrer..." : "Bekreft levering"}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* 6c. Delivered — payout or complaint */}
      {status === "delivered" && (
        <Card className="border-green-200 bg-green-50/40 shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold">Etter levering</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <Button
              className="w-full gap-2 h-9"
              disabled={!!actingStatus}
              onClick={() => {
                setConfirmModal({
                  isOpen: true,
                  title: "Sett til utbetaling",
                  description: "Bekreft at alt er i orden og sett auksjonen til avventer utbetaling.",
                  icon: <Banknote className="h-8 w-8 text-green-600" />,
                  confirmLabel: "Bekreft",
                  onConfirm: async () => {
                    closeConfirm();
                    await transitionStatus("pending_payout");
                  },
                });
              }}
            >
              <Banknote className="h-3.5 w-3.5" />
              {actingStatus === "pending_payout" ? "Lagrer..." : "Sett til avventer utbetaling"}
            </Button>
            <Button
              variant="outline"
              className="w-full gap-2 h-9 bg-background"
              disabled={!!actingStatus}
              onClick={() => {
                setConfirmModal({
                  isOpen: true,
                  variant: "destructive",
                  title: "Registrer reklamasjon",
                  description: "Er du sikker på at du vil opprette en reklamasjon for denne auksjonen?",
                  icon: <AlertTriangle className="h-8 w-8" />,
                  confirmLabel: "Registrer reklamasjon",
                  onConfirm: async () => {
                    closeConfirm();
                    await transitionStatus("complaint_review");
                  },
                });
              }}
            >
              <AlertTriangle className="h-3.5 w-3.5" />
              {actingStatus === "complaint_review" ? "Lagrer..." : "Registrer reklamasjon"}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* 7. Pending payout */}
      {status === "pending_payout" && (
        <Card className="border-green-200 bg-green-50/40 shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold">Utbetaling</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-[11px] text-muted-foreground leading-relaxed italic">
              Bekreft at oppgjør er utbetalt til selger.
            </p>
            <Button
              className="w-full gap-2 h-9"
              disabled={actingStatus === "paid_out"}
              onClick={() => {
                setConfirmModal({
                  isOpen: true,
                  title: "Bekreft utbetaling",
                  description: "Er du sikker på at oppgjør er utbetalt til selger?",
                  icon: <Banknote className="h-8 w-8 text-green-600" />,
                  confirmLabel: "Bekreft utbetaling",
                  onConfirm: async () => {
                    closeConfirm();
                    await confirmPayout();
                  },
                });
              }}
            >
              <Banknote className="h-3.5 w-3.5" />
              {actingStatus === "paid_out" ? "Lagrer..." : "Bekreft utbetaling"}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* 8. Danger Zone */}
      {canDeleteAuction && (
        <Card className="border-destructive/20 shadow-none bg-destructive/5 mt-8">
          <CardHeader className="pb-3">
            <CardTitle className="text-xs font-bold text-destructive/80 uppercase tracking-wider">
              {isNoBids ? "Skjul auksjon" : "Slett auksjon"}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-[11px] text-muted-foreground leading-relaxed italic">
              {isNoBids ? "Auksjonen fikk ingen bud. Den skjules fra lister, men dataene beholdes." : "Kan kun slettes i tidlige faser eller ved kansellering."}
            </p>
            <Button
              variant="destructive"
              className="w-full gap-2 h-8 text-xs"
              disabled={deleting}
              onClick={onDelete}
            >
              <Trash2 className="h-3.5 w-3.5" />
              {isNoBids ? (deleting ? "Skjuler..." : "Skjul auksjon") : (deleting ? "Sletter..." : "Slett auksjon")}
            </Button>
          </CardContent>
        </Card>
      )}

      <AuctionConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={closeConfirm}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        description={confirmModal.description}
        confirmLabel={confirmModal.confirmLabel}
        variant={confirmModal.variant}
        icon={confirmModal.icon}
        isLoading={starting || pausing || deleting}
      />
    </div>
  );
}
